// src/modules/general-task/GeneralTaskActions.tsx
import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEye,
  faEdit,
  faTrash,
  faPlus,
  faFileUpload,
  faComments,
  faChevronDown,
  faChevronUp,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { UploadContexts, EntityType, MODULES } from '../../constants/globalConstants';
import { GeneralTaskDTO, GeneralTaskReportDTO } from './GeneralTaskApi';
import { TaskAssignmentModal } from '../task-assignment/TaskAssignmentModal';

// Permission utils (supports per-action moduleName + multiple permissions with OR)
import {
  applyPermissionsToActions,
  readUserFromLocalStorage,
  PermissionAction,
} from '../../utils/permissions';

interface TaskActionsProps {
  task: GeneralTaskReportDTO;
  levelNumber: string;
  maxLevelReached: boolean;
  handleDelete: (id: string) => void;
  handleCreateTask: (task: GeneralTaskDTO, levelNumber: string) => void;
}

type ActionItem = {
  icon: any;
  label: string;
  className: string;
  onClick: () => void;
  disabled?: boolean;
  moduleName?: string;                                        // per-action module override
  required: PermissionAction | PermissionAction[] | string;   // single or multiple (OR)
};

const GeneralTaskActions: React.FC<TaskActionsProps> = ({
  task,
  levelNumber,
  maxLevelReached,
  handleDelete,
  handleCreateTask,
}) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);

  const user = readUserFromLocalStorage();

  const handleAssignmentClick = () => setShowAssignModal(true);

  // Build the action list; each action can declare its own moduleName
  let actionButtons: ActionItem[] = [
    {
      icon: faEye,
      label: 'View',
      className: 'bg-blue-600 hover:bg-blue-700',
      onClick: () => navigate(`/general-tasks/${task.id}`),
      moduleName: MODULES.GENERAL_TASKS,
      required: 'view',
    },
    {
      icon: faEdit,
      label: 'Edit',
      className: 'bg-yellow-500 hover:bg-yellow-600',
      onClick: () => navigate(`/general-tasks/${task.id}/edit`),
      moduleName: MODULES.GENERAL_TASKS,
      required: 'edit',
    },
    {
      icon: faPlus,
      label: 'Add Sub-Task',
      className: maxLevelReached
        ? 'bg-gray-400 cursor-not-allowed'
        : 'bg-green-600 hover:bg-green-700',
      disabled: maxLevelReached,
      onClick: () => !maxLevelReached && handleCreateTask(task as unknown as GeneralTaskDTO, levelNumber),
      moduleName: MODULES.GENERAL_TASKS,
      required: 'create', // allow if user has either create OR edit
    },
    {
      icon: faTrash,
      label: 'Delete',
      className: 'bg-red-600 hover:bg-red-700',
      onClick: () => handleDelete(task.id),
      moduleName: MODULES.GENERAL_TASKS,
      required: 'delete',
    },
    {
      icon: faFileUpload,
      label: 'Add Docs',
      className: 'bg-purple-600 hover:bg-purple-700',
      onClick: () =>
        navigate('/uploads', {
          state: {
            context: UploadContexts.TASK_COMMENTS_FILES,
            entityId: task.id,
            entityType: EntityType.GENERAL_TASK,
            metadata: {
              taskName: task.title,
              taskLevel: levelNumber,
              status: task.status,
              priority: (task as any).priority ?? null,
            },
            title: 'Upload Supporting Documents for General Task',
            description: 'Upload documents related to this general task.',
            uploadType: 'TASK_SUPPORT_DOCS',
            maxFiles: 10,
            allowedFileTypes: ['application/pdf', 'image/*', 'application/msword', 'video/*'],
          },
        }),
      moduleName: MODULES.UPLOADS, // fallback if UPLOADS not defined
      required: ['create','edit','view'],
    },
    {
      icon: faComments,
      label: 'Add Comments',
      className: 'bg-blue-500 hover:bg-blue-600',
      onClick: () =>
        navigate('/comments', {
          state: {
            context: UploadContexts.TASK_COMMENTS_FILES,
            entityId: task.id,
            entityType: EntityType.GENERAL_TASK,
            metadata: {
              taskName: task.title,
              taskLevel: levelNumber,
              status: task.status,
              priority: (task as any).priority ?? null,
            },
            title: 'Comments for General Task',
            description: 'Comments related to this general task.',
            uploadType: 'TASK_SUPPORT_DOCS',
            maxFiles: 10,
            allowedFileTypes: ['application/pdf', 'image/*', 'application/msword', 'video/*'],
          },
        }),
      moduleName: (MODULES as any).COMMENTS ?? MODULES.GENERAL_TASKS,
      required: ['create','edit','view'],
    },
    {
      icon: faUsers,
      label: 'Assign',
      className: 'bg-indigo-600 hover:bg-indigo-700',
      onClick: handleAssignmentClick,
      moduleName: MODULES.TASK_ASSIGNMENTS, // use edit on General Tasks for assignment
      required: 'create',
    },
  ];

  // Apply permissions: default module = GENERAL_TASKS; hide unauthorized actions (switch to "disable" if preferred)
  actionButtons = applyPermissionsToActions(actionButtons, MODULES.GENERAL_TASKS, 'hide', user);

  return (
    <td className="p-2 border space-y-1">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full bg-gray-200 text-gray-700 px-2 py-1 rounded hover:bg-gray-300 flex items-center justify-center gap-2 text-xs font-medium"
        title={expanded ? 'Hide Actions' : 'Show Actions'}
      >
        <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} />
        {expanded ? 'Hide Actions' : 'Show Actions'}
      </button>

      {expanded ? (
        <div className="space-y-1 transition-all duration-300 ease-in-out">
          {actionButtons.map(({ icon, label, className, onClick, disabled }) => (
            <button
              key={label}
              className={`${className} text-white px-2 py-1 rounded w-full flex items-center justify-center gap-2 disabled:opacity-50`}
              onClick={onClick}
              disabled={!!disabled}
              title={label}
            >
              <FontAwesomeIcon icon={icon} />
              {label}
            </button>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-1 mt-1 transition-all duration-300 ease-in-out">
          {actionButtons.map(({ icon, label, className, onClick, disabled }) => (
            <button
              key={label}
              className={`${className} text-white p-2 rounded flex items-center justify-center disabled:opacity-50`}
              onClick={onClick}
              disabled={!!disabled}
              title={label}
            >
              <FontAwesomeIcon icon={icon} />
            </button>
          ))}
        </div>
      )}

      {showAssignModal && (
        <TaskAssignmentModal
          isOpen={showAssignModal}
          generalTaks={task}              // keeping your existing prop name to avoid breaking API
          entityType={EntityType.GENERAL_TASK}
          taskType="GENERAL_TASK"
          entityId={task.id}
          onClose={() => setShowAssignModal(false)}
          task={null}
        />
      )}
    </td>
  );
};

export default GeneralTaskActions;